/**
 * ItemTile - Unified tile component for all item types
 *
 * Displays items in a card format with:
 * - Row 1: Icon + (X items) left, Check/Eye/Trash buttons right
 * - Row 2-3: Name (max 2 lines with truncation)
 * - Row 4: Due date (non-recurring) OR Recurrence stats (recurring, blue text)
 * - Row 5: Effort: X hrs • Priority: XXX
 * - Expandable sub-items and attachments section
 */

import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Eye, EyeOff, Trash2, CheckSquare, DollarSign, FileText, Calendar, Paperclip } from 'lucide-react';
import { clsx } from 'clsx';
import { useItemStore, useAuthStore, useUIStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import {
  formatRelativeDate,
  capitalize,
  getNoteColorValue,
  getDefaultStatus,
  getCompleteStatus,
  isCompleteStatus,
  isRecurringItem,
  effortHoursToDisplay,
  isOccurrenceComplete,
  getDefaultOccurrenceStatus,
  getCompleteOccurrenceStatus,
} from '@perfin/shared';
import type { Item, SubItem, ItemType, Attachment, Occurrence } from '@perfin/shared';
import type { TabKey } from '@/stores/uiStore';

// ============================================================================
// TYPES
// ============================================================================

interface ItemTileProps {
  item: Item;
  linkPath: string;
  currentTab?: TabKey;
  occurrenceDate?: Date;       // Which occurrence this tile represents (for recurring items)
  hideStatusToggle?: boolean;  // Hide tick/trash buttons (e.g., for All Recurring tab)
}

// ============================================================================
// HELPERS
// ============================================================================

const priorityLabels: Record<string, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  future: 'Future',
};

function getPriorityLabel(priority: string | undefined): string {
  if (!priority) return 'Medium';
  return priorityLabels[priority] || capitalize(priority);
}

function getItemStatus(item: Item): 'complete' | 'incomplete' {
  const status = (item as unknown as { status?: string }).status;
  return isCompleteStatus(status) ? 'complete' : 'incomplete';
}

function getSubItemStatus(subItem: SubItem): 'complete' | 'incomplete' {
  const status = (subItem as unknown as { status?: string }).status;
  return isCompleteStatus(status) ? 'complete' : 'incomplete';
}

function getItemTypeLabel(type: ItemType): string {
  switch (type) {
    case 'task': return 'Task';
    case 'transaction': return 'Transaction';
    case 'note': return 'Note';
    case 'keyDate': return 'Date';
    default: return 'Item';
  }
}

const TYPE_ICON_CONFIG = {
  task: { icon: CheckSquare, color: 'text-blue-600' },
  transaction: { icon: DollarSign, color: 'text-green-600' },
  note: { icon: FileText, color: 'text-amber-600' },
  keyDate: { icon: Calendar, color: 'text-purple-600' },
} as const;

// Priority badge colors
const PRIORITY_CONFIG: Record<string, { bg: string; text: string }> = {
  critical: { bg: 'bg-red-100', text: 'text-red-700' },
  high: { bg: 'bg-orange-100', text: 'text-orange-700' },
  medium: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  low: { bg: 'bg-green-100', text: 'text-green-700' },
  future: { bg: 'bg-gray-100', text: 'text-gray-600' },
};

// Helper to check if two dates are the same day
function isSameDay(d1: Date, d2: Date): boolean {
  return d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();
}

// ============================================================================
// SUB-ITEM ROW COMPONENT
// ============================================================================

interface SubItemRowProps {
  subItem: SubItem;
  parentId: string;
  parentCreatedBy: string;
  occurrenceDate?: Date;  // For recurring sub-items, which occurrence this represents
  hideStatusToggle?: boolean;  // Hide tick/trash buttons (e.g., for All Recurring tab)
}

function SubItemRow({ subItem, parentId, parentCreatedBy, occurrenceDate, hideStatusToggle }: SubItemRowProps) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { editSubItem, removeSubItem } = useItemStore();
  const toast = useToast();
  const { confirmDelete } = useConfirm();

  const dueDate = (subItem as unknown as { dueDate?: { toDate: () => Date } }).dueDate;
  const effortHours = (subItem as unknown as { effortHours?: number }).effortHours;
  const config = TYPE_ICON_CONFIG[subItem.type];
  const Icon = config.icon;
  const isRecurring = isRecurringItem(subItem);
  const occurrences = (subItem as unknown as { occurrences?: Occurrence[] }).occurrences || [];

  // Determine completion status (occurrence-aware for recurring items)
  const isComplete = (() => {
    if (isRecurring && occurrenceDate) {
      const occ = occurrences.find(o => {
        const occDate = o.date instanceof Date ? o.date : (o.date as unknown as { toDate: () => Date }).toDate();
        return isSameDay(occDate, occurrenceDate);
      });
      return occ ? isOccurrenceComplete(occ.status) : false;
    }
    return getSubItemStatus(subItem) === 'complete';
  })();

  const handleToggleStatus = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;

    try {
      // For recurring sub-items with occurrenceDate, update specific occurrence
      if (isRecurring && occurrenceDate) {
        const updatedOccurrences = [...occurrences];
        const occIndex = updatedOccurrences.findIndex(o => {
          const occDate = o.date instanceof Date ? o.date : (o.date as unknown as { toDate: () => Date }).toDate();
          return isSameDay(occDate, occurrenceDate);
        });

        if (occIndex >= 0) {
          if (isComplete) {
            updatedOccurrences[occIndex] = {
              ...updatedOccurrences[occIndex],
              status: getDefaultOccurrenceStatus(subItem.type),
              completedAt: undefined,
              finalizedAt: undefined,
            };
          } else {
            const now = new Date();
            updatedOccurrences[occIndex] = {
              ...updatedOccurrences[occIndex],
              status: getCompleteOccurrenceStatus(subItem.type),
              ...(subItem.type === 'task' ? { completedAt: now } : { finalizedAt: now }),
            };
          }
        } else {
          const now = new Date();
          updatedOccurrences.push({
            date: occurrenceDate,
            status: getCompleteOccurrenceStatus(subItem.type),
            ...(subItem.type === 'task' ? { completedAt: now } : { finalizedAt: now }),
          });
        }

        await editSubItem(parentCreatedBy, parentId, subItem.id, { occurrences: updatedOccurrences });
        toast.success(isComplete ? `${getItemTypeLabel(subItem.type)} reopened` : `${getItemTypeLabel(subItem.type)} completed!`);
        return;
      }

      // Non-recurring: update item-level status
      const currentStatus = (subItem as unknown as { status?: string }).status;
      const lastKnownStatus = (subItem as unknown as { lastKnownStatus?: string }).lastKnownStatus;

      if (isComplete) {
        const restoredStatus = lastKnownStatus || getDefaultStatus(subItem.type) || 'draft';
        await editSubItem(parentCreatedBy, parentId, subItem.id, { status: restoredStatus as 'new' | 'inProgress' | 'complete' | 'draft' | 'pending' | 'final', lastKnownStatus: undefined });
      } else {
        const completeStatus = getCompleteStatus(subItem.type) as 'complete' | 'final';
        await editSubItem(parentCreatedBy, parentId, subItem.id, { status: completeStatus, lastKnownStatus: currentStatus });
      }
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    confirmDelete({
      title: `Delete ${getItemTypeLabel(subItem.type)}`,
      message: `Are you sure you want to delete "${subItem.name}" and all its notifications? This action cannot be undone.`,
      onConfirm: async () => {
        if (!user) return;
        try {
          await removeSubItem(parentCreatedBy, parentId, subItem.id);
          toast.success(`${getItemTypeLabel(subItem.type)} deleted`);
        } catch {
          toast.error('Failed to delete');
        }
      },
    });
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/items/${parentId}?subItem=${subItem.id}`);
  };

  return (
    <div
      className="py-2 px-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
      onClick={handleClick}
    >
      {/* Row 1: Icon + Actions */}
      <div className="flex items-center justify-between mb-1">
        <Icon className={clsx('h-4 w-4', config.color)} />
        {!hideStatusToggle && (
          <div className="flex items-center gap-1">
            <button
              onClick={handleToggleStatus}
              className="p-1 rounded transition-colors hover:bg-gray-200"
              title={isComplete ? 'Mark incomplete' : 'Mark complete'}
            >
              <Check
                className={clsx('h-4 w-4', isComplete ? 'text-green-500' : 'text-gray-400')}
                strokeWidth={3}
              />
            </button>
            <button
              onClick={handleDelete}
              className="p-1 rounded transition-colors hover:bg-red-50 hover:text-red-600 text-gray-400"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Row 2-3: Name (max 2 lines) */}
      <p className={clsx(
        'font-semibold text-sm text-black line-clamp-2',
        isComplete && 'line-through opacity-60'
      )}>
        {subItem.name}
      </p>

      {/* Row 4: Date display */}
      <p className="text-xs text-black opacity-70 mt-1">
        {dueDate ? `Due: ${formatRelativeDate(dueDate.toDate())}` : 'No due date'}
      </p>

      {/* Row 5: Effort + Priority */}
      <p className="text-xs text-black opacity-70 mt-1">
        Effort: {effortHoursToDisplay(effortHours)} • Priority: {getPriorityLabel(subItem.priority)}
      </p>
    </div>
  );
}

// ============================================================================
// ATTACHMENT ROW COMPONENT
// ============================================================================

interface AttachmentRowProps {
  attachment: Attachment;
  parentId: string;
  parentCreatedBy: string;
}

function AttachmentRow({ attachment, parentId, parentCreatedBy }: AttachmentRowProps) {
  const { user } = useAuthStore();
  const { removeItemAttachment } = useItemStore();
  const toast = useToast();
  const { confirmDelete } = useConfirm();

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    confirmDelete({
      title: 'Delete Attachment',
      message: `Are you sure you want to delete "${attachment.name}"? This action cannot be undone.`,
      onConfirm: async () => {
        if (!user) return;
        try {
          await removeItemAttachment(parentCreatedBy, parentId, attachment);
          toast.success('Attachment deleted');
        } catch {
          toast.error('Failed to delete attachment');
        }
      },
    });
  };

  return (
    <div
      className="py-2 px-3 bg-gray-50 rounded-lg"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Row 1: Icon + Delete */}
      <div className="flex items-center justify-between mb-1">
        <Paperclip className="h-4 w-4 text-gray-500" />
        <button
          onClick={handleDelete}
          className="p-1 rounded transition-colors hover:bg-red-50 hover:text-red-600 text-gray-400"
          title="Delete"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Row 2: File name */}
      <p className="text-sm text-black truncate">{attachment.name}</p>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ItemTile({ item, linkPath, occurrenceDate, hideStatusToggle }: ItemTileProps) {
  const { user } = useAuthStore();
  const { editItem, removeItem, subItemsMap, subscribeToItemSubItems } = useItemStore();
  const toast = useToast();
  const { confirmDelete } = useConfirm();
  const expandedItemIds = useUIStore((state) => state.expandedItemIds);
  const toggleItemExpanded = useUIStore((state) => state.toggleItemExpanded);

  const expanded = expandedItemIds.includes(item.id);

  // Subscribe to sub-items when expanded
  useEffect(() => {
    if (!expanded || !user || !item.id) return;
    const unsubscribe = subscribeToItemSubItems(item.createdBy, item.id);
    return unsubscribe;
  }, [expanded, user, item.id, item.createdBy, subscribeToItemSubItems]);

  const subItems = subItemsMap[item.id] || [];
  const subItemCount = item.subItemCount ?? 0;
  const attachments = (item as unknown as { attachments?: Attachment[] }).attachments || [];
  const attachmentCount = attachments.length;
  const totalChildCount = subItemCount + attachmentCount;
  const hasChildren = totalChildCount > 0;

  const itemName = item.name || 'Untitled';
  const dueDate = (item as unknown as { dueDate?: { toDate: () => Date } }).dueDate;
  const effortHours = (item as unknown as { effortHours?: number }).effortHours;
  const noteColor = item.type === 'note' ? (item as unknown as { color?: string }).color : undefined;
  const bgColor = item.type === 'note' ? getNoteColorValue((noteColor || 'default') as Parameters<typeof getNoteColorValue>[0]) : undefined;
  const isRecurring = isRecurringItem(item);
  const occurrences = (item as unknown as { occurrences?: Occurrence[] }).occurrences || [];

  // For recurring items with occurrenceDate, check specific occurrence status
  // Otherwise fall back to item-level status
  const isComplete = (() => {
    if (isRecurring && occurrenceDate) {
      const occ = occurrences.find(o => {
        const occDate = o.date instanceof Date ? o.date : (o.date as unknown as { toDate: () => Date }).toDate();
        return isSameDay(occDate, occurrenceDate);
      });
      return occ ? isOccurrenceComplete(occ.status) : false;
    }
    return getItemStatus(item) === 'complete';
  })();

  const handleToggleStatus = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;

    try {
      // For recurring items with occurrenceDate, update specific occurrence
      if (isRecurring && occurrenceDate) {
        const updatedOccurrences = [...occurrences];
        const occIndex = updatedOccurrences.findIndex(o => {
          const occDate = o.date instanceof Date ? o.date : (o.date as unknown as { toDate: () => Date }).toDate();
          return isSameDay(occDate, occurrenceDate);
        });

        if (occIndex >= 0) {
          // Toggle existing occurrence
          if (isComplete) {
            updatedOccurrences[occIndex] = {
              ...updatedOccurrences[occIndex],
              status: getDefaultOccurrenceStatus(item.type),
              completedAt: undefined,
              finalizedAt: undefined,
            };
          } else {
            const now = new Date();
            updatedOccurrences[occIndex] = {
              ...updatedOccurrences[occIndex],
              status: getCompleteOccurrenceStatus(item.type),
              ...(item.type === 'task' ? { completedAt: now } : { finalizedAt: now }),
            };
          }
        } else {
          // Create new occurrence entry
          const now = new Date();
          updatedOccurrences.push({
            date: occurrenceDate,
            status: getCompleteOccurrenceStatus(item.type),
            ...(item.type === 'task' ? { completedAt: now } : { finalizedAt: now }),
          });
        }

        await editItem(item.createdBy, item.id, { occurrences: updatedOccurrences });
        toast.success(isComplete ? `${getItemTypeLabel(item.type)} reopened` : `${getItemTypeLabel(item.type)} completed!`);
        return;
      }

      // Non-recurring: update item-level status
      const currentStatus = (item as unknown as { status?: string }).status;
      const lastKnownStatus = (item as unknown as { lastKnownStatus?: string }).lastKnownStatus;

      if (isComplete) {
        // Restore previous status from lastKnownStatus, or default
        const restoredStatus = lastKnownStatus || getDefaultStatus(item.type) || 'draft';
        await editItem(item.createdBy, item.id, { status: restoredStatus as 'new' | 'inProgress' | 'complete' | 'draft' | 'pending' | 'final', lastKnownStatus: undefined });
        toast.success(`${getItemTypeLabel(item.type)} reopened`);
      } else {
        // Save current status before marking complete/final
        const completeStatus = getCompleteStatus(item.type) as 'complete' | 'final';
        await editItem(item.createdBy, item.id, { status: completeStatus, lastKnownStatus: currentStatus });
        toast.success(`${getItemTypeLabel(item.type)} completed!`);
      }
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleItemExpanded(item.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const typeLabel = getItemTypeLabel(item.type);
    const isRecurring = item.recurrence && item.recurrence !== 'none';
    confirmDelete({
      title: isRecurring ? `Delete Recurring ${typeLabel}` : `Delete ${typeLabel}`,
      message: isRecurring
        ? <span>You are about to delete a Recurring {typeLabel}. This will permanently delete <strong>All Occurrences</strong> and <strong>all notifications</strong> for this {typeLabel.toLowerCase()}, and it cannot be reversed. Do you wish to proceed?</span>
        : <span>You are about to permanently delete a single-instance {typeLabel} and all its notifications. Do you wish to proceed?</span>,
      onConfirm: async () => {
        if (!user) return;
        try {
          await removeItem(item.createdBy, item.id);
          toast.success(`${typeLabel} deleted`);
        } catch {
          toast.error('Failed to delete');
        }
      },
    });
  };

  return (
    <Link
      to={linkPath}
      className={clsx(
        'block rounded-xl border hover:shadow-md transition-shadow overflow-hidden',
        (dueDate || isRecurring) ? 'border-gray-200' : 'border-dashed border-gray-300'
      )}
      style={{ backgroundColor: bgColor || 'white' }}
    >
      <div className="p-4">
        {/* Row 1: Icon + (X items) left, buttons right */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {(() => {
              const config = TYPE_ICON_CONFIG[item.type];
              const Icon = config.icon;
              return <Icon className={clsx('h-4 w-4 flex-shrink-0', config.color)} />;
            })()}
            {/* Recurrence frequency badge */}
            {isRecurring && (
              <span className="text-xs text-blue-600 font-medium">
                {capitalize((item as unknown as { recurrence?: string }).recurrence || 'none')}
              </span>
            )}
            {/* Chore badge */}
            {(item as unknown as { choreWheel?: boolean }).choreWheel && (
              <span className="text-xs text-white bg-purple-600 px-1.5 py-0.5 rounded font-medium">
                Chore
              </span>
            )}
            {/* Priority Badge */}
            {item.priority && PRIORITY_CONFIG[item.priority] && (
              <span className={clsx(
                'px-1.5 py-0.5 text-xs font-medium rounded',
                PRIORITY_CONFIG[item.priority].bg,
                PRIORITY_CONFIG[item.priority].text
              )}>
                {priorityLabels[item.priority] || capitalize(item.priority)}
              </span>
            )}
            {totalChildCount > 0 && (
              <span className="text-xs text-gray-400">
                ({totalChildCount} {totalChildCount === 1 ? 'item' : 'items'})
              </span>
            )}
          </div>

          {/* Action buttons: View, Complete, Trash */}
          <div className="flex items-center gap-1">
            {hasChildren && (
              <button
                onClick={handleToggleExpand}
                className="p-1 rounded transition-colors hover:bg-gray-100"
                title={expanded ? 'Hide children' : 'Show children'}
              >
                {expanded ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            )}

            {!hideStatusToggle && (
              <>
                <button
                  onClick={handleToggleStatus}
                  className="p-1 rounded transition-colors hover:bg-gray-100"
                  title={isComplete ? 'Mark incomplete' : 'Mark complete'}
                >
                  <Check
                    className={clsx('h-4 w-4', isComplete ? 'text-green-500' : 'text-gray-400')}
                    strokeWidth={3}
                  />
                </button>

                <button
                  onClick={handleDelete}
                  className="p-1 rounded transition-colors hover:bg-red-50 hover:text-red-600 text-gray-400"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Row 2-3: Name (max 2 lines) */}
        <h3 className={clsx(
          'font-semibold text-black line-clamp-2 mb-1',
          isComplete && 'line-through opacity-60'
        )}>
          {itemName}
        </h3>

        {/* Row 4: Date display based on item type and context */}
        {isRecurring && occurrenceDate ? (
          <p className="text-sm text-black opacity-70">
            Occurrence Date: {occurrenceDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        ) : isRecurring ? null : (
          <p className="text-sm text-black opacity-70">
            {dueDate ? `Due: ${formatRelativeDate(dueDate.toDate())}` : 'No due date'}
          </p>
        )}

        {/* Row 5: Effort + Priority */}
        <p className="text-sm text-black opacity-70">
          Effort: {effortHoursToDisplay(effortHours)} • Priority: {getPriorityLabel(item.priority)}
        </p>

        {/* Expanded children (sub-items + attachments) */}
        {expanded && hasChildren && (
          <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
            {/* Sub-items */}
            {subItemCount > 0 && subItems.length === 0 ? (
              <p className="text-xs text-gray-400">Loading sub-items...</p>
            ) : (
              subItems.map((subItem) => (
                <SubItemRow
                  key={subItem.id}
                  subItem={subItem}
                  parentId={item.id}
                  parentCreatedBy={item.createdBy}
                  occurrenceDate={occurrenceDate}
                  hideStatusToggle={hideStatusToggle}
                />
              ))
            )}
            {/* Attachments */}
            {attachments.map((attachment) => (
              <AttachmentRow
                key={attachment.id}
                attachment={attachment}
                parentId={item.id}
                parentCreatedBy={item.createdBy}
              />
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}
